import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { useCanManageOrganizationApi } from "@hooks/permissions/organization/useCanManageOrganizationApi";
import { useCanViewOrganizationBilling } from "@hooks/permissions/organization/useCanViewOrganizationBilling";
import { useCanViewOrganizationMemberAccessLog } from "@hooks/permissions/organization/useCanViewOrganizationMemberAccessLog";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    CliIcon,
    CogwheelIcon,
    DollarCircleIcon,
    IntegrationsIcon,
    LockIcon,
    MembersIcon,
} from "@icons";
import { useOrgNavigationShortcutKeys } from "../useResolvedOrgNavigationShortcuts";
import type { SideNavSectionState } from "./sideNavSection.types";
import { useOpenFirmSettings } from "./useOpenFirmSettings";

/**
 * The firm's own pages, each gated like its page, then Settings, which opens
 * the firm Profile settings. Team is always shown, so the section is never empty.
 */
export const FirmSection = ({ section }: { section: SideNavSectionState }) => {
    const canViewBilling = useCanViewOrganizationBilling();
    const canManageApi = useCanManageOrganizationApi();
    const canViewMemberAccessLog = useCanViewOrganizationMemberAccessLog();
    const openFirmSettings = useOpenFirmSettings();
    const shortcutKeys = useOrgNavigationShortcutKeys();
    const {
        organizationLevelIntegrations: isOrganizationIntegrationsEnabled,
        organizationMemberAccessLog: isMemberAccessLogEnabled,
    } = useTypedFlags();

    return (
        <SideNavSection {...section}>
            {canViewBilling && (
                <SideNavItem
                    to="/organization-billing"
                    icon={<DollarCircleIcon />}
                    label="Billing"
                    shortcut={shortcutKeys("billing")}
                />
            )}
            <SideNavItem
                to="/team"
                icon={<MembersIcon />}
                label="Team"
                shortcut={shortcutKeys("team")}
            />
            {isOrganizationIntegrationsEnabled && (
                <SideNavItem
                    to="/organization-integrations"
                    icon={<IntegrationsIcon />}
                    label="Integrations"
                />
            )}
            {canManageApi && (
                <SideNavItem
                    to="/organization-api"
                    icon={<CliIcon />}
                    label="API"
                    shortcut={shortcutKeys("api")}
                />
            )}
            {isMemberAccessLogEnabled && canViewMemberAccessLog && (
                <SideNavItem
                    to="/organization-member-access-log"
                    icon={<LockIcon />}
                    label="Access log"
                />
            )}
            {openFirmSettings && (
                <SideNavItem
                    onClick={openFirmSettings}
                    icon={<CogwheelIcon />}
                    label="Settings"
                    shortcut={shortcutKeys("settings")}
                />
            )}
        </SideNavSection>
    );
};
